import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useNavigationType, useSearchParams } from 'react-router-dom';
import { api, formatTl } from '../lib/api';
import { UNIT_LABEL, type StockChannel, type Unit } from '@artmozaik/shared';
import { useAuth } from '../lib/auth';
import Ipucu from '../ui/Ipucu';
import AltSayfa from '../ui/AltSayfa';
import { useTelefon } from '../ui/mobil';
import StokDetay from './StokDetay';
import Sayim from './Sayim';
import FiyatListesi from './FiyatListesi';
import ExcelAktar from './ExcelAktar';
import Ikon from './urun/ikon';
import StokDuzelt from './urun/StokDuzelt';
import { KATEGORI_AD, donusYaz, indir, miktar, siraYaz, yerel } from './urun/ortak';
import { sor } from '../ui/Sor';

/**
 * ÜRÜNLER VE STOK (`/stok`) — Shopify "Envanter" düzeni.
 *
 * Tek satırlık araç çubuğu (görünüm · ara ve filtrele · sütunlar · sıralama),
 * görselli ferah satırlar, sağa yaslı sayı sütunları, altta sayfalama hapı.
 * Satıra tıklayınca TAM ürün kartı açılır; hızlı bakış çekmecesi satırdaki göz
 * düğmesinde (ya da boşluk tuşunda). Stok sayısına tıklayınca hızlı düzeltme.
 *
 * Süzgeçler adres çubuğunda taşınır: karttan geri dönünce aynı yere gelinir.
 */
type Satir = {
  id: number; code: string; name: string; unit: Unit; notes: string | null;
  category: string; boxM2: number | null;
  official: number; offrec: number | null; toplam: number; threshold: number;
  listPriceKurus: number | null; maxDiscountPct: number | null;
  aliases: { code: string; supplier: string | null }[];
  sonAlis: { kurus: number; supplier: string | null; deltaPct: number | null } | null;
  durum: 'kritik' | 'esikte' | 'saglikli';
  kritik: boolean;
  brand: string | null; imageUrl: string | null; active: boolean; fasonda: number | null;
  /** Yalnız maliyeti görebilen rolde gelir. */
  maliyetBilinmiyor?: boolean;
};
type Veri = {
  satirlar: Satir[];
  ozet: {
    urun: number; kritik: number; esikte: number; tukendi: number;
    faturasizGorunur: boolean; maliyetGorunur: boolean;
    fasonGorunur: boolean; fasonda: number; pasif: number; markalar?: string[];
  };
};
type Bekleyen = { faturaAdedi: number; satirAdedi: number; kaynakHazir: boolean };

const GORUNUMLER = [
  { id: 'tumu', ad: 'Tümü' }, { id: 'kritik', ad: 'Kritik' }, { id: 'esikte', ad: 'Eşikte' },
  { id: 'tukendi', ad: 'Tükendi' }, { id: 'fasonda', ad: 'Fasonda' }, { id: 'pasif', ad: 'Pasif' },
] as const;
type Gorunum = (typeof GORUNUMLER)[number]['id'];

type SutunId = 'kod' | 'tedarikci' | 'resmi' | 'faturasiz' | 'fasonda' | 'toplam' | 'esik' | 'liste' | 'sonAlis';
const SUTUNLAR: { id: SutunId; ad: string; varsayilanGizli?: boolean }[] = [
  { id: 'kod', ad: 'Kod' }, { id: 'tedarikci', ad: 'Tedarikçi kodu', varsayilanGizli: true },
  { id: 'resmi', ad: 'Resmî stok' }, { id: 'faturasiz', ad: 'Faturasız' }, { id: 'fasonda', ad: 'Fasonda' },
  { id: 'toplam', ad: 'Toplam' }, { id: 'esik', ad: 'Eşik' }, { id: 'liste', ad: 'Liste fiyatı' }, { id: 'sonAlis', ad: 'Son alış', varsayilanGizli: true },
];
const SUTUN_ANAHTAR = 'am_stok_sutun_gizli';

const SIRALAR = [
  { id: 'eksik', ad: 'Önerilen (eksiği en çok olan önce)' }, { id: 'kod', ad: 'Kod' }, { id: 'ad', ad: 'Ad' },
  { id: 'toplam', ad: 'Toplam stok' }, { id: 'resmi', ad: 'Resmî stok' }, { id: 'liste', ad: 'Liste fiyatı' },
] as const;
type Sira = (typeof SIRALAR)[number]['id'];

const SAYFA = 50;
const trK = (a: string, b: string) => a.localeCompare(b, 'tr', { numeric: true });

function gizliOku(): Set<SutunId> {
  try {
    const v = JSON.parse(localStorage.getItem(SUTUN_ANAHTAR) ?? 'null');
    if (Array.isArray(v)) return new Set(v as SutunId[]);
  } catch { /* bozuk kayıt: varsayılana dön */ }
  return new Set(SUTUNLAR.filter((s) => s.varsayilanGizli).map((s) => s.id));
}

export default function Stok() {
  const { can } = useAuth();
  const git = useNavigate();
  const [sorgu, setSorgu] = useSearchParams();

  /* ── Süzgeçler adres çubuğunda ── */
  const gorunum = (GORUNUMLER.some((g) => g.id === sorgu.get('g')) ? sorgu.get('g') : 'tumu') as Gorunum;
  const araSorgu = sorgu.get('ara') ?? '';
  const kategori = sorgu.get('kat') ?? '';
  const marka = sorgu.get('marka') ?? '';
  const gorselYok = sorgu.get('gy') === '1';
  const fiyatYok = sorgu.get('fy') === '1';
  const maliyetYok = sorgu.get('my') === '1';
  const sira = (SIRALAR.some((s) => s.id === sorgu.get('s')) ? sorgu.get('s') : 'eksik') as Sira;
  const azalan = sorgu.get('y') === 'a';
  const sayfa = Math.max(1, Number(sorgu.get('p')) || 1);
  const degis = useCallback((d: Record<string, string | null>, sayfayiKoru = false) => {
    setSorgu((eski) => {
      const p = new URLSearchParams(eski);
      for (const [k, v] of Object.entries(d)) { if (v == null || v === '') p.delete(k); else p.set(k, v); }
      if (!sayfayiKoru) p.delete('p');
      return p;
    }, { replace: true });
  }, [setSorgu]);

  const [ara, setAra] = useState(araSorgu);
  const [veri, setVeri] = useState<Veri | null>(null);
  const [bekleyen, setBekleyen] = useState<Bekleyen | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [bilgi, setBilgi] = useState<string | null>(null);
  const [araniyor, setAraniyor] = useState(false);
  const [cekmece, setCekmece] = useState<number | null>(null);
  const [sayimAcik, setSayimAcik] = useState(false);
  const [fiyatAcik, setFiyatAcik] = useState(false);
  const [aktarAcik, setAktarAcik] = useState(false);
  const [indiriliyor, setIndiriliyor] = useState(false);
  const [secili, setSecili] = useState<Set<number>>(new Set());
  const [odak, setOdak] = useState(-1);
  const [duzelt, setDuzelt] = useState<{ id: number; kanal: StockChannel } | null>(null);
  const [acik, setAcik] = useState<null | 'gorunum' | 'filtre' | 'sutun' | 'sirala'>(null);
  const [gizli, setGizli] = useState<Set<SutunId>>(gizliOku);
  const [topluIs, setTopluIs] = useState<string | null>(null);
  const tabloRef = useRef<HTMLDivElement>(null);
  /* Telefon düzeni: büyük arama + kart listesi (aynı veri, aynı süzgeçler). */
  const telefon = useTelefon();
  const gelis = useNavigationType();
  const [telSayfa, setTelSayfa] = useState<null | 'filtre' | 'islem'>(null);
  const [telAdet, setTelAdet] = useState(30);

  // Yazdıkça ara: adres çubuğu 250 ms sonra güncellenir.
  useEffect(() => {
    if (ara.trim() === araSorgu) return;
    const t = setTimeout(() => degis({ ara: ara.trim() || null }), 250);
    return () => clearTimeout(t);
  }, [ara, araSorgu, degis]);

  const istekSira = useRef(0);
  const yukle = useCallback(() => {
    const p = new URLSearchParams({ pasifDahil: '1', limit: '2000', sirala: 'kod', durum: 'hepsi', kategori: kategori || 'hepsi' });
    if (araSorgu) p.set('ara', araSorgu);
    if (marka) p.set('marka', marka);
    if (gorselYok) p.set('gorselYok', '1');
    if (fiyatYok) p.set('fiyatYok', '1');
    if (maliyetYok) p.set('maliyetYok', '1');
    setAraniyor(true);
    const no = ++istekSira.current;
    return api<Veri>(`/api/urunler?${p}`)
      .then((v) => { if (no === istekSira.current) { setVeri(v); setHata(null); } })   // geç gelen eski cevap yenisini ezmesin
      .catch((e) => setHata((e as Error).message))
      .finally(() => { if (no === istekSira.current) setAraniyor(false); });
  }, [araSorgu, kategori, marka, gorselYok, fiyatYok, maliyetYok]);
  useEffect(() => { void yukle(); }, [yukle]);

  // "Faturaya bağla" — bekleyen alış satırı sayısı.
  useEffect(() => {
    if (!can('purchase.view')) return;
    api<Bekleyen>('/api/alis/bekleyen').then(setBekleyen).catch(() => undefined);
  }, [can]);

  const faturasizVar = veri?.ozet.faturasizGorunur ?? false;
  const maliyetVar = veri?.ozet.maliyetGorunur ?? false;
  const fasonVar = veri?.ozet.fasonGorunur ?? false;
  const stokYazar = can('stock.write');
  const urunYazar = can('product.write');

  /* ── Görünüm → sıralama → sayfa ── */
  const suzulmus = useMemo(() => {
    if (!veri) return [];
    const aktif = veri.satirlar.filter((s) => s.active);
    const liste = gorunum === 'pasif' ? veri.satirlar.filter((s) => !s.active)
      : gorunum === 'kritik' ? aktif.filter((s) => s.durum === 'kritik')
      : gorunum === 'esikte' ? aktif.filter((s) => s.durum === 'esikte')
      : gorunum === 'tukendi' ? aktif.filter((s) => s.toplam <= 0)
      : gorunum === 'fasonda' ? aktif.filter((s) => (s.fasonda ?? 0) > 0)
      : aktif;
    const k: Record<Sira, (a: Satir, b: Satir) => number> = {
      eksik: (a, b) => a.toplam - a.threshold - (b.toplam - b.threshold) || trK(a.code, b.code),
      kod: (a, b) => trK(a.code, b.code),
      ad: (a, b) => trK(a.name, b.name),
      toplam: (a, b) => a.toplam - b.toplam || trK(a.code, b.code),
      resmi: (a, b) => a.official - b.official || trK(a.code, b.code),
      liste: (a, b) => (a.listPriceKurus ?? -1) - (b.listPriceKurus ?? -1) || trK(a.code, b.code),
    };
    const sirali = [...liste].sort(k[sira]);
    return azalan ? sirali.reverse() : sirali;
  }, [veri, gorunum, sira, azalan]);

  const sayfaAdet = Math.max(1, Math.ceil(suzulmus.length / SAYFA));
  const gecerliSayfa = Math.min(sayfa, sayfaAdet);
  const gorunen = useMemo(() => suzulmus.slice((gecerliSayfa - 1) * SAYFA, gecerliSayfa * SAYFA), [suzulmus, gecerliSayfa]);

  // Ürün kartındaki önceki / sonraki okları ve "geri" hapı bu listeyi izler.
  useEffect(() => { siraYaz(suzulmus.map((s) => s.id)); }, [suzulmus]);
  useEffect(() => { donusYaz(sorgu.toString() ? `?${sorgu}` : ''); }, [sorgu]);
  useEffect(() => { setOdak(-1); }, [gecerliSayfa, gorunum, veri]);

  const sayac: Record<Gorunum, number | null> = veri
    ? { tumu: veri.ozet.urun, kritik: veri.ozet.kritik, esikte: veri.ozet.esikte, tukendi: veri.ozet.tukendi, fasonda: veri.ozet.fasonda, pasif: veri.ozet.pasif }
    : { tumu: null, kritik: null, esikte: null, tukendi: null, fasonda: null, pasif: null };
  const gorunumler = GORUNUMLER.filter((g) => g.id !== 'fasonda' || fasonVar);

  const sutunVar = (id: SutunId) =>
    !gizli.has(id) && (id !== 'faturasiz' || faturasizVar) && (id !== 'fasonda' || fasonVar) && (id !== 'sonAlis' || maliyetVar);
  const sutunDegis = (id: SutunId) => setGizli((eski) => {
    const y = new Set(eski); if (y.has(id)) y.delete(id); else y.add(id);
    try { localStorage.setItem(SUTUN_ANAHTAR, JSON.stringify([...y])); } catch { /* dolu */ }
    return y;
  });

  /* ── Açılır kutular dışarı tıklanınca kapanır ── */
  useEffect(() => {
    if (!acik) return;
    const kapat = (e: MouseEvent) => { if (!(e.target as HTMLElement).closest('.ccd-sl-acilir-kap')) setAcik(null); };
    document.addEventListener('mousedown', kapat);
    return () => document.removeEventListener('mousedown', kapat);
  }, [acik]);

  /* ── Klavye: ↑↓ satır, Enter kartı açar, boşluk hızlı bakış ── */
  const katmanAcik = cekmece != null || sayimAcik || fiyatAcik || aktarAcik || duzelt != null || acik != null;
  useEffect(() => {
    const tus = (e: KeyboardEvent) => {
      if (katmanAcik || e.metaKey || e.ctrlKey || e.altKey) return;
      const h = e.target as HTMLElement | null;
      if (h && (['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON', 'A'].includes(h.tagName) || h.isContentEditable)) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setOdak((o) => {
          const y = Math.max(0, Math.min(gorunen.length - 1, o + (e.key === 'ArrowDown' ? 1 : -1)));
          tabloRef.current?.querySelector<HTMLElement>(`[data-satir="${y}"]`)?.scrollIntoView({ block: 'nearest' });
          return y;
        });
      } else if (e.key === 'Enter' && odak >= 0 && gorunen[odak]) { e.preventDefault(); git(`/stok/${gorunen[odak].id}`); }
      else if (e.key === ' ' && odak >= 0 && gorunen[odak]) { e.preventDefault(); setCekmece(gorunen[odak].id); }
    };
    document.addEventListener('keydown', tus);
    return () => document.removeEventListener('keydown', tus);
  }, [katmanAcik, gorunen, odak, git]);

  /* ── Dışa aktarım (tümü ya da seçilenler) ── */
  async function disaAktar(ids?: number[]) {
    setIndiriliyor(true); setHata(null);
    try {
      const tarih = new Date().toLocaleDateString('tr-TR').replace(/\./g, '-');
      await indir(`/api/urunler/disa-aktar${ids?.length ? `?ids=${ids.join(',')}` : ''}`, `art-mozaik-stok-${ids?.length ? 'secilen-' : ''}${tarih}.xlsx`);
    } catch (e) { setHata((e as Error).message); }
    finally { setIndiriliyor(false); }
  }

  /* ── Çoklu seçim ── */
  const seciliListe = useMemo(() => (veri ? veri.satirlar.filter((s) => secili.has(s.id)) : []), [veri, secili]);
  const sayfaSecili = gorunen.length > 0 && gorunen.every((s) => secili.has(s.id));
  const secDegis = (id: number) => setSecili((eski) => { const y = new Set(eski); if (y.has(id)) y.delete(id); else y.add(id); return y; });
  const sayfayiSec = () => setSecili((eski) => {
    const y = new Set(eski);
    if (sayfaSecili) gorunen.forEach((s) => y.delete(s.id)); else gorunen.forEach((s) => y.add(s.id));
    return y;
  });

  /** Toplu yazma: API'de toplu uç yok; seçilen her ürün için mevcut tekil güncelleme ucu sırayla çağrılır. */
  async function topluYaz(ad: string, govde: Record<string, unknown>) {
    const hedefler = seciliListe;
    let tamam = 0; const olmayan: string[] = [];
    setHata(null); setBilgi(null);
    for (let n = 0; n < hedefler.length; n++) {
      setTopluIs(`${ad}: ${n + 1} / ${hedefler.length}`);
      try { await api(`/api/urunler/${hedefler[n].id}`, { method: 'PATCH', body: JSON.stringify(govde) }); tamam++; }
      catch (e) { olmayan.push(`${hedefler[n].code} (${(e as Error).message})`); }
    }
    setTopluIs(null);
    setBilgi(`${ad}: ${tamam} ürün güncellendi.`);
    if (olmayan.length) setHata(`${olmayan.length} ürün güncellenemedi: ${olmayan.slice(0, 5).join(', ')}${olmayan.length > 5 ? '…' : ''}`);
    setSecili(new Set());
    await yukle();
  }
  async function topluEsik() {
    const oku = (v: string) => Number(v.trim().replace(',', '.'));
    const v = await sor({
      baslik: 'Uyarı eşiğini ayarla', alt: `Seçilen ${seciliListe.length} ürün`, aciklama: 'Stok bu sayının altına düşünce uyarı üretilir. Seçilen bütün ürünlere aynı eşik yazılır.',
      etiket: 'Uyarı eşiği', tur: 'sayi', yerTutucu: 'Örn. 10', zorunlu: true, azami: 12, onay: 'Eşiği uygula',
      denetle: (x) => (/^\d+([.,]\d+)?$/.test(x) && Number.isFinite(oku(x)) ? null : 'Eşik 0 ya da daha büyük bir sayı olmalı.'),
    });
    if (v == null) return;
    void topluYaz('Eşik', { threshold: oku(v) });
  }
  function topluAktiflik(aktif: boolean) {
    if (!window.confirm(aktif ? `Seçilen ${seciliListe.length} ürün yeniden aktif edilsin mi?`
      : `Seçilen ${seciliListe.length} ürün pasife alınsın mı? Listelerde ve teklif aramasında görünmezler; geçmişleri silinmez.`)) return;
    void topluYaz(aktif ? 'Aktifleştirme' : 'Pasife alma', { active: aktif });
  }

  const siraTikla = (id: Sira) => degis(sira === id ? { y: azalan ? null : 'a' } : { s: id === 'eksik' ? null : id, y: null });
  const siraOk = (id: Sira) => (sira === id ? <span className="ccd-sl-ok" aria-hidden="true">{azalan ? '↓' : '↑'}</span> : null);
  const ariaSira = (id: Sira) => (sira === id ? (azalan ? 'descending' : 'ascending') : undefined);

  const filtreAdet = [kategori, marka, gorselYok, fiyatYok, maliyetYok].filter(Boolean).length;
  const filtreTemizle = () => degis({ kat: null, marka: null, gy: null, fy: null, my: null });

  if (hata && !veri) {
    return (
      <div className="ccd-banner ccd-banner-danger">
        <span className="ccd-banner-icon" aria-hidden="true">!</span><span>{hata}</span>
      </div>
    );
  }

  const duzeltilen = duzelt ? veri?.satirlar.find((s) => s.id === duzelt.id) ?? null : null;

  const katmanlar = (
    <>
      {cekmece != null && (
        <StokDetay
          id={cekmece}
          kapat={() => setCekmece(null)}
          degisti={() => void yukle()}
          yazabilir={stokYazar}
          faturasizYazabilir={can('informal.write')}
          kodDegistirebilir={can('stock.code_change')}
        />
      )}
      {aktarAcik && <ExcelAktar kapat={() => setAktarAcik(false)} bitti={() => { setAktarAcik(false); void yukle(); }} />}
      {sayimAcik && <Sayim kapat={() => { setSayimAcik(false); void yukle(); }} />}
      {fiyatAcik && <FiyatListesi kapat={() => setFiyatAcik(false)} bitti={() => void yukle()} />}
    </>
  );

  /* ═══ TELEFON (≤640 px): stok arama ═══════════════════════════════════════
     Büyük arama kutusu, sonuç kartları (görsel · ad · kod · stok rozetleri), karta dokununca
     özet sayfası (stok, fiyat, son hareketler) ve oradan "Karta git". Masaüstündeki bütün
     eylemler "İşlemler" sayfasında durur; hiçbiri kaybolmaz. */
  if (telefon) {
    const telGorunen = suzulmus.slice(0, telAdet);
    return (
      <div className="ccd-sa">
        <div className="ccd-sa-ust">
          <label className="ccd-sa-ara" data-tur="ara">
            <Ikon ad="ara" boy={20} />
            {/* İleri gelişte odaklı açılır (klavye hazır); geri dönüşte liste kaldığı yerde kalsın diye odaklanmaz. */}
            <input type="search" inputMode="search" enterKeyHint="search" autoComplete="off" autoCapitalize="none" autoCorrect="off" spellCheck={false}
                   autoFocus={gelis !== 'POP' && !araSorgu} value={ara} onChange={(e) => { setAra(e.target.value); setTelAdet(30); }}
                   placeholder="Kod, ad, marka ya da takma ad" aria-label="Ürünlerde ara"
                   onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
            {araniyor && <span className="ccd-oneri-donen" aria-label="Aranıyor" />}
            {ara && !araniyor && <button type="button" className="ccd-sa-sil" aria-label="Aramayı temizle" onClick={() => setAra('')}><Ikon ad="carpi" boy={18} /></button>}
          </label>
          <div className="ccd-sa-serit" role="tablist" aria-label="Görünüm" data-tur="suzgec">
            {gorunumler.map((g) => (
              <button type="button" key={g.id} role="tab" aria-selected={gorunum === g.id} className={`ccd-sa-cip${gorunum === g.id ? ' ccd-is-active' : ''}`}
                      onClick={() => { degis({ g: g.id === 'tumu' ? null : g.id }); setTelAdet(30); }}>
                {g.ad}{sayac[g.id] != null && <span className="ccd-sa-cip-sayi">{sayac[g.id]}</span>}
              </button>
            ))}
          </div>
          <div className="ccd-sa-araclar">
            <button type="button" className="ccd-btn ccd-sa-arac" onClick={() => setTelSayfa('filtre')}>
              <Ikon ad="sirala" boy={18} /> Filtre ve sıralama{filtreAdet > 0 && <span className="ccd-sl-sayac ccd-sl-sayac-dolu">{filtreAdet}</span>}
            </button>
            <button type="button" className="ccd-btn ccd-sa-arac" onClick={() => setTelSayfa('islem')} aria-label="İşlemler: ürün ekle, içe ve dışa aktar, sayım">
              İşlemler{bekleyen && bekleyen.faturaAdedi > 0 && <span className="ccd-sl-sayac ccd-sl-sayac-uyari">{bekleyen.faturaAdedi}</span>}
            </button>
          </div>
        </div>

        {bilgi && <div className="ccd-banner"><span className="ccd-banner-icon" aria-hidden="true">✓</span><span>{bilgi}</span></div>}
        {hata && <div className="ccd-banner ccd-banner-danger" role="alert"><span className="ccd-banner-icon" aria-hidden="true">!</span><span>{hata}</span></div>}

        {!veri ? <div className="ccd-sl-bos">Yükleniyor…</div> : suzulmus.length === 0 ? (
          <div className="ccd-sl-bos">
            {araSorgu ? `"${araSorgu}" için sonuç yok.` : 'Bu görünümde ürün yok.'}
            {(filtreAdet > 0 || gorunum !== 'tumu') && (
              <button type="button" className="ccd-btn" onClick={() => degis({ g: null, kat: null, marka: null, gy: null, fy: null, my: null })}>Süzgeçleri kaldır</button>
            )}
          </div>
        ) : (
          <>
            <div className="ccd-sa-sayim">
              <span>{suzulmus.length} ürün{araSorgu ? ` · "${araSorgu}"` : ''}</span>
              <button type="button" className="ccd-sa-yenile" onClick={() => void yukle()} disabled={araniyor}>Yenile</button>
            </div>
            <ul className="ccd-sa-liste">
              {telGorunen.map((s) => {
                const stokTutar = s.category !== 'HIZMET';
                const tukendi = s.active && stokTutar && s.toplam <= 0;
                const kritik = s.active && stokTutar && !tukendi && s.durum === 'kritik';
                return (
                  <li key={s.id}>
                    <button type="button" className={`ccd-sa-kart${s.active ? '' : ' ccd-sa-pasif'}`} onClick={() => setCekmece(s.id)} aria-label={`${s.name} — ${s.code} özetini aç`}>
                      {s.imageUrl
                        ? <img className="ccd-sa-gorsel" src={yerel(s.imageUrl)} alt="" loading="lazy" />
                        : <span className="ccd-sa-gorsel ccd-sl-gorsel-yok" aria-hidden="true"><Ikon ad="gorsel" boy={22} /></span>}
                      <span className="ccd-sa-metin">
                        <span className="ccd-sa-ad">{s.name}</span>
                        <span className="ccd-sa-kod ccd-mono">{s.code}{s.brand ? <span className="ccd-sa-marka"> · {s.brand}</span> : null}</span>
                        <span className="ccd-sa-rozetler">
                          {tukendi && <span className="ccd-badge ccd-badge-danger"><span className="ccd-badge-dot" />tükendi</span>}
                          {kritik && <span className="ccd-badge ccd-badge-warning"><span className="ccd-badge-dot" />kritik</span>}
                          {!s.active && <span className="ccd-badge">pasif</span>}
                          {stokTutar && <span className="ccd-sa-rozet">Resmî <b>{miktar(s.official)}</b></span>}
                          {stokTutar && faturasizVar && (s.offrec ?? 0) !== 0 && <span className="ccd-sa-rozet ccd-sa-rozet-fz">Faturasız <b>{miktar(s.offrec ?? 0)}</b></span>}
                          {fasonVar && (s.fasonda ?? 0) > 0 && <span className="ccd-sa-rozet">Fasonda <b>{miktar(s.fasonda!)}</b></span>}
                        </span>
                      </span>
                      <span className="ccd-sa-sag">
                        {stokTutar ? <><b className="ccd-sa-toplam">{miktar(s.toplam)}</b><span className="ccd-sa-birim">{UNIT_LABEL[s.unit]}</span></> : <span className="ccd-sa-birim">hizmet</span>}
                        {s.listPriceKurus != null && <span className="ccd-sa-fiyat">{formatTl(s.listPriceKurus)}</span>}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {suzulmus.length > telGorunen.length && (
              <button type="button" className="ccd-btn ccd-btn-block ccd-sa-daha" onClick={() => setTelAdet((a) => a + 30)}>
                Daha fazla göster ({suzulmus.length - telGorunen.length} ürün daha)
              </button>
            )}
          </>
        )}

        {telSayfa === 'filtre' && (
          <AltSayfa baslik="Filtre ve sıralama" kapat={() => setTelSayfa(null)}
                    eylem={<>
                      <button type="button" className="ccd-btn" onClick={() => { filtreTemizle(); degis({ s: null, y: null }); }}>Temizle</button>
                      <button type="button" className="ccd-btn ccd-btn-primary" onClick={() => setTelSayfa(null)}>{suzulmus.length} ürünü göster</button>
                    </>}>
            <div className="ccd-field">
              <label className="ccd-field-label" htmlFor="sa-kat">Kategori</label>
              <select id="sa-kat" className="ccd-select" value={kategori} onChange={(e) => degis({ kat: e.target.value || null })}>
                <option value="">Tüm kategoriler</option>
                {Object.entries(KATEGORI_AD).map(([k, ad]) => <option key={k} value={k}>{ad}</option>)}
              </select>
            </div>
            <div className="ccd-field">
              <label className="ccd-field-label" htmlFor="sa-marka">Marka</label>
              <select id="sa-marka" className="ccd-select" value={marka} onChange={(e) => degis({ marka: e.target.value || null })}>
                <option value="">Tüm markalar</option>
                {marka && !(veri?.ozet.markalar ?? []).includes(marka) && <option value={marka}>{marka}</option>}
                {(veri?.ozet.markalar ?? []).map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <label className="ccd-sl-onay"><input type="checkbox" checked={gorselYok} onChange={(e) => degis({ gy: e.target.checked ? '1' : null })} /> Görseli yok</label>
            <label className="ccd-sl-onay"><input type="checkbox" checked={fiyatYok} onChange={(e) => degis({ fy: e.target.checked ? '1' : null })} /> Liste fiyatı yok</label>
            {maliyetVar && <label className="ccd-sl-onay"><input type="checkbox" checked={maliyetYok} onChange={(e) => degis({ my: e.target.checked ? '1' : null })} /> Maliyeti bilinmiyor</label>}
            <div className="ccd-field">
              <label className="ccd-field-label" htmlFor="sa-sira">Sıralama</label>
              <select id="sa-sira" className="ccd-select" value={sira} onChange={(e) => degis({ s: e.target.value === 'eksik' ? null : e.target.value })}>
                {SIRALAR.map((x) => <option key={x.id} value={x.id}>{x.ad}</option>)}
              </select>
            </div>
            <div className="ccd-sd-secim" role="group" aria-label="Sıra yönü">
              <button type="button" className={!azalan ? 'ccd-is-active' : ''} aria-pressed={!azalan} onClick={() => degis({ y: null })}>Artan</button>
              <button type="button" className={azalan ? 'ccd-is-active' : ''} aria-pressed={azalan} onClick={() => degis({ y: 'a' })}>Azalan</button>
            </div>
          </AltSayfa>
        )}
        {telSayfa === 'islem' && (
          <AltSayfa baslik="İşlemler" alt="Ürünler ve stok" kapat={() => setTelSayfa(null)}>
            <div className="ccd-sa-islemler">
              {urunYazar && <Link className="ccd-ustmenu-oge" to="/stok/yeni" data-tur="yeni-urun"><Ikon ad="arti" /><span>Ürün ekle</span></Link>}
              {can('purchase.match') && (
                <Link className="ccd-ustmenu-oge" to="/alis"><Ikon ad="bag" /><span>Faturaya bağla
                  <small>{bekleyen && bekleyen.faturaAdedi > 0 ? `${bekleyen.faturaAdedi} alış faturasında ${bekleyen.satirAdedi} satır bekliyor` : 'eşleşme bekleyen fatura yok'}</small></span></Link>
              )}
              {stokYazar && <button type="button" className="ccd-ustmenu-oge" data-tur="sayim" onClick={() => { setTelSayfa(null); setSayimAcik(true); }}><Ikon ad="sayim" /><span>Sayım <small>fiili adet gir, farkı işle</small></span></button>}
              {can('stock.import') && <button type="button" className="ccd-ustmenu-oge" data-tur="fiyat-listesi" onClick={() => { setTelSayfa(null); setFiyatAcik(true); }}><Ikon ad="fiyat" /><span>Fiyat listesi <small>tedarikçi listesinden toplu güncelle</small></span></button>}
              {can('stock.import') && <button type="button" className="ccd-ustmenu-oge" data-tur="excel" onClick={() => { setTelSayfa(null); setAktarAcik(true); }}><Ikon ad="ice" /><span>İçe aktar <small>Excel ile ürün ve stok</small></span></button>}
              <button type="button" className="ccd-ustmenu-oge" disabled={indiriliyor} onClick={() => { setTelSayfa(null); void disaAktar(); }}><Ikon ad="disa" /><span>{indiriliyor ? 'Hazırlanıyor…' : 'Dışa aktar'} <small>tüm aktif ürünler, Excel</small></span></button>
            </div>
          </AltSayfa>
        )}
        {katmanlar}
      </div>
    );
  }

  const stokHucre = (s: Satir, kanal: StockChannel, deger: number, sinif: string) => {
    const yazilir = stokYazar && s.active && s.category !== 'HIZMET' && (kanal === 'OFFICIAL' || can('informal.write'));
    const acikMi = duzelt?.id === s.id && duzelt.kanal === kanal;
    return (
      <td className={`ccd-sl-sayi ${sinif}`} onClick={yazilir ? (e) => e.stopPropagation() : undefined}>
        {yazilir ? (
          <span className="ccd-sl-duzelt-kap">
            <button type="button" className="ccd-sl-stok-dugme" title="Stok düzelt" aria-label={`${s.code} ${kanal === 'OFFICIAL' ? 'resmî' : 'faturasız'} stok ${miktar(deger)} — düzelt`}
                    onClick={() => setDuzelt(acikMi ? null : { id: s.id, kanal })}>{miktar(deger)}</button>
            {acikMi && duzeltilen && (
              <StokDuzelt urun={duzeltilen} kanal={kanal} faturasizYazabilir={can('informal.write')} kapat={() => setDuzelt(null)}
                          bitti={(r) => { setDuzelt(null); setBilgi(`${s.code} · ${r.kanal === 'OFFICIAL' ? 'resmî' : 'faturasız'}: ${miktar(r.oncesi)} → ${miktar(r.sonrasi)}`); void yukle(); }} />
            )}
          </span>
        ) : deger === 0 && kanal === 'OFFREC' ? <span className="ccd-muted">—</span> : miktar(deger)}
      </td>
    );
  };

  return (
    <div className="ccd-sl">
      {/* ── Başlık + eylem hapları ── */}
      <div className="ccd-sl-ust">
        <div className="ccd-sl-baslik-kap">
          <span className="ccd-sl-baslik-ikon" aria-hidden="true"><Ikon ad="kutu" boy={20} /></span>
          <h1 className="ccd-sl-baslik">Ürünler ve Stok</h1>
        </div>
        <div className="ccd-sl-haplar">
          <button className="ccd-btn ccd-btn-sm ccd-sp-hap" onClick={() => void disaAktar()} disabled={indiriliyor} title="Tüm aktif ürünleri Excel'e aktar">
            {indiriliyor ? 'Hazırlanıyor…' : 'Dışa aktar'}
          </button>
          {can('stock.import') && (
            <button className="ccd-btn ccd-btn-sm ccd-sp-hap" onClick={() => setAktarAcik(true)} data-tur="excel" title="Excel ile ürün ve stok güncelle (önce önizleme)">İçe aktar</button>
          )}
          {can('stock.import') && (
            <button className="ccd-btn ccd-btn-sm ccd-sp-hap" onClick={() => setFiyatAcik(true)} data-tur="fiyat-listesi"
                    title="Tedarikçi fiyat listesinden liste fiyatlarını toplu güncelle (önce önizleme)">Fiyat listesi</button>
          )}
          {stokYazar && (
            <button className="ccd-btn ccd-btn-sm ccd-sp-hap" onClick={() => setSayimAcik(true)} data-tur="sayim"
                    title="Listeyi gezerek fiili adet gir, farkları toplu sayım hareketi olarak işle">Sayım</button>
          )}
          {can('purchase.match') && (
            <Link className="ccd-btn ccd-btn-sm ccd-sp-hap" to="/alis"
                  title={bekleyen && bekleyen.faturaAdedi > 0
                    ? `${bekleyen.faturaAdedi} alış faturasında ${bekleyen.satirAdedi} satır ürüne bağlanmayı bekliyor`
                    : 'Alış faturası satırlarını ürüne bağla. Şu an eşleşme bekleyen fatura yok.'}>
              Faturaya bağla{bekleyen && bekleyen.faturaAdedi > 0 && <span className="ccd-sl-sayac ccd-sl-sayac-uyari">{bekleyen.faturaAdedi}</span>}
            </Link>
          )}
          {urunYazar && (
            <Link className="ccd-btn ccd-btn-primary ccd-btn-sm ccd-sp-hap" to="/stok/yeni" data-tur="yeni-urun"><Ikon ad="arti" boy={14} kalin /> Ürün ekle</Link>
          )}
        </div>
      </div>

      {bilgi && <div className="ccd-banner"><span className="ccd-banner-icon" aria-hidden="true">✓</span><span>{bilgi}</span></div>}
      {hata && <div className="ccd-banner ccd-banner-danger" role="alert"><span className="ccd-banner-icon" aria-hidden="true">!</span><span>{hata}</span></div>}

      <div className="ccd-sl-kart">
        {/* ── Araç çubuğu (tek satır, yapışkan) ── */}
        <div className="ccd-sl-arac">
          <div className="ccd-sl-acilir-kap" data-tur="suzgec">
            <button type="button" className="ccd-sl-arac-dugme ccd-sl-gorunum" aria-haspopup="listbox" aria-expanded={acik === 'gorunum'}
                    onClick={() => setAcik(acik === 'gorunum' ? null : 'gorunum')}>
              {GORUNUMLER.find((g) => g.id === gorunum)!.ad}
              {sayac[gorunum] != null && <span className="ccd-sl-sayac">{sayac[gorunum]}</span>}
              <Ikon ad="acilir" boy={14} />
            </button>
            {acik === 'gorunum' && (
              <div className="ccd-sl-acilir" role="listbox" aria-label="Görünüm">
                {gorunumler.map((g) => (
                  <button type="button" key={g.id} role="option" aria-selected={gorunum === g.id} className={gorunum === g.id ? 'ccd-is-active' : ''}
                          onClick={() => { degis({ g: g.id === 'tumu' ? null : g.id }); setAcik(null); }}>
                    <span>{g.ad}</span><span className="ccd-sl-sayac">{sayac[g.id] ?? '…'}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="ccd-sl-ara" data-tur="ara">
            <Ikon ad="ara" boy={16} />
            <input value={ara} onChange={(e) => setAra(e.target.value)} placeholder="Ara ve filtrele: kod, ad, marka, takma ad…" aria-label="Ürünlerde ara" />
            {araniyor && <span className="ccd-oneri-donen" aria-label="Aranıyor" />}
            {ara && !araniyor && <button type="button" className="ccd-sl-ara-sil" aria-label="Aramayı temizle" onClick={() => setAra('')}><Ikon ad="carpi" boy={14} /></button>}
          </div>

          <div className="ccd-sl-acilir-kap">
            <button type="button" className="ccd-sl-arac-dugme" aria-haspopup="dialog" aria-expanded={acik === 'filtre'} onClick={() => setAcik(acik === 'filtre' ? null : 'filtre')}>
              Filtre{filtreAdet > 0 && <span className="ccd-sl-sayac ccd-sl-sayac-dolu">{filtreAdet}</span>} <Ikon ad="acilir" boy={14} />
            </button>
            {acik === 'filtre' && (
              <div className="ccd-sl-acilir ccd-sl-acilir-genis ccd-sl-acilir-sag" role="dialog" aria-label="Filtreler">
                <div className="ccd-field">
                  <label className="ccd-field-label" htmlFor="sl-kat">Kategori</label>
                  <select id="sl-kat" className="ccd-select" value={kategori} onChange={(e) => degis({ kat: e.target.value || null })}>
                    <option value="">Tüm kategoriler</option>
                    {Object.entries(KATEGORI_AD).map(([k, ad]) => <option key={k} value={k}>{ad}</option>)}
                  </select>
                </div>
                <div className="ccd-field">
                  <label className="ccd-field-label" htmlFor="sl-marka">Marka</label>
                  <select id="sl-marka" className="ccd-select" value={marka} onChange={(e) => degis({ marka: e.target.value || null })}>
                    <option value="">Tüm markalar</option>
                    {marka && !(veri?.ozet.markalar ?? []).includes(marka) && <option value={marka}>{marka}</option>}
                    {(veri?.ozet.markalar ?? []).map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                  {(veri?.ozet.markalar ?? []).length === 0 && <div className="ccd-field-hint">Henüz hiçbir ürüne marka yazılmamış.</div>}
                </div>
                <label className="ccd-sl-onay"><input type="checkbox" checked={gorselYok} onChange={(e) => degis({ gy: e.target.checked ? '1' : null })} /> Görseli yok</label>
                <label className="ccd-sl-onay"><input type="checkbox" checked={fiyatYok} onChange={(e) => degis({ fy: e.target.checked ? '1' : null })} /> Liste fiyatı yok</label>
                {maliyetVar && (
                  <label className="ccd-sl-onay"><input type="checkbox" checked={maliyetYok} onChange={(e) => degis({ my: e.target.checked ? '1' : null })} /> Maliyeti bilinmiyor</label>
                )}
              </div>
            )}
          </div>

          <div className="ccd-sl-acilir-kap">
            <button type="button" className="ccd-sl-arac-dugme ccd-sl-arac-ikon" aria-label="Sütunlar" title="Sütunları göster / gizle" aria-expanded={acik === 'sutun'}
                    onClick={() => setAcik(acik === 'sutun' ? null : 'sutun')}><Ikon ad="sutun" /></button>
            {acik === 'sutun' && (
              <div className="ccd-sl-acilir ccd-sl-acilir-sag" role="dialog" aria-label="Sütunlar">
                {SUTUNLAR.filter((s) => (s.id !== 'faturasiz' || faturasizVar) && (s.id !== 'fasonda' || fasonVar) && (s.id !== 'sonAlis' || maliyetVar)).map((s) => (
                  <label className="ccd-sl-onay" key={s.id}><input type="checkbox" checked={!gizli.has(s.id)} onChange={() => sutunDegis(s.id)} /> {s.ad}</label>
                ))}
              </div>
            )}
          </div>
          <div className="ccd-sl-acilir-kap">
            <button type="button" className="ccd-sl-arac-dugme ccd-sl-arac-ikon" aria-label="Sırala" title="Sırala" aria-expanded={acik === 'sirala'}
                    onClick={() => setAcik(acik === 'sirala' ? null : 'sirala')}><Ikon ad="sirala" /></button>
            {acik === 'sirala' && (
              <div className="ccd-sl-acilir ccd-sl-acilir-sag" role="listbox" aria-label="Sıralama">
                {SIRALAR.map((s) => (
                  <button type="button" key={s.id} role="option" aria-selected={sira === s.id} className={sira === s.id ? 'ccd-is-active' : ''}
                          onClick={() => degis({ s: s.id === 'eksik' ? null : s.id })}><span>{s.ad}</span></button>
                ))}
                <div className="ccd-sl-acilir-ayrac" />
                <button type="button" role="option" aria-selected={!azalan} className={!azalan ? 'ccd-is-active' : ''} onClick={() => degis({ y: null })}><span>Artan (küçükten büyüğe)</span></button>
                <button type="button" role="option" aria-selected={azalan} className={azalan ? 'ccd-is-active' : ''} onClick={() => degis({ y: 'a' })}><span>Azalan (büyükten küçüğe)</span></button>
              </div>
            )}
          </div>
        </div>

        {/* ── Etkin süzgeç çipleri ── */}
        {filtreAdet > 0 && (
          <div className="ccd-sl-cipler">
            {kategori && <button type="button" className="ccd-sl-cip" onClick={() => degis({ kat: null })}>Kategori: {KATEGORI_AD[kategori] ?? kategori} <Ikon ad="carpi" boy={12} /></button>}
            {marka && <button type="button" className="ccd-sl-cip" onClick={() => degis({ marka: null })}>Marka: {marka} <Ikon ad="carpi" boy={12} /></button>}
            {gorselYok && <button type="button" className="ccd-sl-cip" onClick={() => degis({ gy: null })}>Görseli yok <Ikon ad="carpi" boy={12} /></button>}
            {fiyatYok && <button type="button" className="ccd-sl-cip" onClick={() => degis({ fy: null })}>Liste fiyatı yok <Ikon ad="carpi" boy={12} /></button>}
            {maliyetYok && <button type="button" className="ccd-sl-cip" onClick={() => degis({ my: null })}>Maliyeti bilinmiyor <Ikon ad="carpi" boy={12} /></button>}
            <button type="button" className="ccd-sl-cip-temizle" onClick={filtreTemizle}>Tümünü temizle</button>
          </div>
        )}

        {/* ── Çoklu seçim şeridi ── */}
        {secili.size > 0 && (
          <div className="ccd-sl-toplu" role="region" aria-label="Seçilen ürünler">
            <span className="ccd-sl-toplu-adet">{topluIs ?? `${secili.size} ürün seçildi`}</span>
            {suzulmus.length > gorunen.length && secili.size < suzulmus.length && !topluIs && (
              <button type="button" className="ccd-sl-toplu-bag" onClick={() => setSecili(new Set(suzulmus.map((s) => s.id)))}>Bu görünümdeki {suzulmus.length} ürünün tümünü seç</button>
            )}
            <span className="ccd-sl-toplu-dugmeler">
              <button type="button" className="ccd-btn ccd-btn-sm ccd-sp-hap" disabled={indiriliyor || !!topluIs} onClick={() => void disaAktar([...secili])}>Seçilenleri dışa aktar</button>
              {(stokYazar || urunYazar) && <button type="button" className="ccd-btn ccd-btn-sm ccd-sp-hap" disabled={!!topluIs} onClick={topluEsik}>Eşik ayarla</button>}
              {urunYazar && seciliListe.some((s) => s.active) && <button type="button" className="ccd-btn ccd-btn-sm ccd-sp-hap" disabled={!!topluIs} onClick={() => topluAktiflik(false)}>Pasife al</button>}
              {urunYazar && seciliListe.some((s) => !s.active) && <button type="button" className="ccd-btn ccd-btn-sm ccd-sp-hap" disabled={!!topluIs} onClick={() => topluAktiflik(true)}>Aktifleştir</button>}
              <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm ccd-sp-hap" disabled={!!topluIs} onClick={() => setSecili(new Set())}>Seçimi kaldır</button>
            </span>
          </div>
        )}

        {/* ── Tablo ── */}
        {!veri ? (
          <div className="ccd-sl-bos">Yükleniyor…</div>
        ) : suzulmus.length === 0 ? (
          <div className="ccd-sl-bos">
            {araSorgu ? `"${araSorgu}" için sonuç yok.` : 'Bu görünümde ürün yok.'}
            {(filtreAdet > 0 || gorunum !== 'tumu') && (
              <button type="button" className="ccd-sl-cip-temizle" onClick={() => degis({ g: null, kat: null, marka: null, gy: null, fy: null, my: null })}>Süzgeçleri kaldır</button>
            )}
          </div>
        ) : (
          <div className="ccd-sl-tablo-kap" ref={tabloRef}>
            <table className="ccd-sl-tablo">
              <thead>
                <tr>
                  <th className="ccd-sl-sec"><input type="checkbox" aria-label="Bu sayfadaki ürünlerin tümünü seç" checked={sayfaSecili} onChange={sayfayiSec} /></th>
                  <th className="ccd-sl-gorsel-b" aria-label="Görsel" />
                  <th aria-sort={ariaSira('ad')}><button type="button" className="ccd-sl-baslik-dugme" onClick={() => siraTikla('ad')}>Ürün{siraOk('ad')}</button></th>
                  {sutunVar('kod') && <th aria-sort={ariaSira('kod')}><button type="button" className="ccd-sl-baslik-dugme" onClick={() => siraTikla('kod')}>Kod{siraOk('kod')}</button></th>}
                  {sutunVar('tedarikci') && <th><span className="ccd-sl-noktali">Tedarikçi kodu</span> <Ipucu id="esleme-kuyrugu" /></th>}
                  {sutunVar('resmi') && <th className="ccd-sl-sayi" aria-sort={ariaSira('resmi')}><button type="button" className="ccd-sl-baslik-dugme ccd-sl-noktali" onClick={() => siraTikla('resmi')}>Resmî stok{siraOk('resmi')}</button> <Ipucu id="resmi-stok" /></th>}
                  {sutunVar('faturasiz') && <th className="ccd-sl-sayi"><span className="ccd-sl-noktali">Faturasız</span> <Ipucu id="faturasiz-stok" /></th>}
                  {sutunVar('fasonda') && <th className="ccd-sl-sayi"><span className="ccd-sl-noktali">Fasonda</span> <Ipucu id="fasondaki-stok" /></th>}
                  {sutunVar('toplam') && <th className="ccd-sl-sayi" aria-sort={ariaSira('toplam')}><button type="button" className="ccd-sl-baslik-dugme ccd-sl-noktali" onClick={() => siraTikla('toplam')}>Toplam{siraOk('toplam')}</button> <Ipucu id="toplam-stok" /></th>}
                  {sutunVar('esik') && <th className="ccd-sl-sayi"><span className="ccd-sl-noktali">Eşik</span> <Ipucu id="esik" /></th>}
                  {sutunVar('liste') && <th className="ccd-sl-sayi" aria-sort={ariaSira('liste')}><button type="button" className="ccd-sl-baslik-dugme" onClick={() => siraTikla('liste')}>Liste fiyatı{siraOk('liste')}</button></th>}
                  {sutunVar('sonAlis') && <th className="ccd-sl-sayi"><span className="ccd-sl-noktali">Son alış</span> <Ipucu id="son-alis" /></th>}
                  <th className="ccd-sl-goz-b" aria-label="Hızlı bakış" />
                </tr>
              </thead>
              <tbody>
                {gorunen.map((s, n) => {
                  const tukendi = s.active && s.category !== 'HIZMET' && s.toplam <= 0;
                  const kritik = s.active && s.category !== 'HIZMET' && !tukendi && s.durum === 'kritik';
                  const alt = [s.brand, KATEGORI_AD[s.category] ?? s.category, s.notes].filter(Boolean).join(' · ');
                  return (
                    <tr key={s.id} data-satir={n}
                        className={`ccd-sl-satir${secili.has(s.id) ? ' ccd-sl-secili' : ''}${odak === n ? ' ccd-sl-odak' : ''}${s.active ? '' : ' ccd-sl-pasif'}`}
                        onClick={() => git(`/stok/${s.id}`)}>
                      <td className="ccd-sl-sec" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" aria-label={`${s.code} seç`} checked={secili.has(s.id)} onChange={() => secDegis(s.id)} />
                      </td>
                      <td className="ccd-sl-gorsel-h">
                        {s.imageUrl
                          ? <img className="ccd-sl-gorsel" src={yerel(s.imageUrl)} alt="" loading="lazy" />
                          : <span className="ccd-sl-gorsel ccd-sl-gorsel-yok" aria-hidden="true"><Ikon ad="gorsel" boy={18} /></span>}
                      </td>
                      <td className="ccd-sl-urun">
                        <div className="ccd-sl-urun-ad">
                          <Link to={`/stok/${s.id}`} onClick={(e) => e.stopPropagation()}>{s.name}</Link>
                          {tukendi && <span className="ccd-badge ccd-badge-danger" title="Toplam stok sıfır"><span className="ccd-badge-dot" />tükendi</span>}
                          {kritik && <span className="ccd-badge ccd-badge-warning" title="Toplam stok eşiğin altında"><span className="ccd-badge-dot" />kritik</span>}
                          {!s.active && <span className="ccd-badge">pasif</span>}
                        </div>
                        <div className="ccd-sl-urun-alt">
                          <span className="ccd-sl-mobil-kod ccd-mono">{s.code}</span>{alt}
                        </div>
                      </td>
                      {sutunVar('kod') && <td className="ccd-sl-kod ccd-mono">{s.code}</td>}
                      {sutunVar('tedarikci') && (
                        <td className="ccd-sl-tedarikci">
                          {s.aliases.length === 0 ? <span className="ccd-muted">{s.category === 'URETIM' ? 'üretim içi' : '—'}</span>
                            : s.aliases.filter((a) => !a.code.startsWith('parasut:')).map((a) => (
                              <div key={a.code}>{a.supplier ? `${a.supplier} · ` : ''}<span className="ccd-mono">{a.code}</span></div>
                            ))}
                        </td>
                      )}
                      {sutunVar('resmi') && stokHucre(s, 'OFFICIAL', s.official, 'ccd-kanal-official')}
                      {sutunVar('faturasiz') && stokHucre(s, 'OFFREC', s.offrec ?? 0, 'ccd-kanal-offrec')}
                      {sutunVar('fasonda') && <td className="ccd-sl-sayi">{(s.fasonda ?? 0) > 0 ? miktar(s.fasonda!) : <span className="ccd-muted">—</span>}</td>}
                      {sutunVar('toplam') && <td className="ccd-sl-sayi ccd-sl-toplam">{miktar(s.toplam)}<span className="ccd-sl-birim">{UNIT_LABEL[s.unit]}</span></td>}
                      {sutunVar('esik') && <td className="ccd-sl-sayi ccd-sl-soluk">{miktar(s.threshold)}</td>}
                      {sutunVar('liste') && <td className="ccd-sl-sayi">{s.listPriceKurus != null ? formatTl(s.listPriceKurus) : <span className="ccd-muted">—</span>}</td>}
                      {sutunVar('sonAlis') && (
                        <td className="ccd-sl-sayi">
                          {!s.sonAlis ? <span className="ccd-muted">—</span> : (
                            <>
                              {formatTl(s.sonAlis.kurus)}
                              {s.sonAlis.deltaPct != null && s.sonAlis.deltaPct !== 0 && (
                                <span className={s.sonAlis.deltaPct > 0 ? 'ccd-kpi-down' : 'ccd-kpi-up'} style={{ marginLeft: 6 }}>{s.sonAlis.deltaPct > 0 ? '+' : ''}%{s.sonAlis.deltaPct}</span>
                              )}
                            </>
                          )}
                        </td>
                      )}
                      <td className="ccd-sl-goz-h" onClick={(e) => e.stopPropagation()}>
                        <button type="button" className="ccd-sl-goz" aria-label={`${s.code} hızlı bakış`} title="Hızlı bakış (boşluk)" onClick={() => setCekmece(s.id)}><Ikon ad="goz" /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Sayfalama ── */}
        {veri && suzulmus.length > 0 && (
          <div className="ccd-sl-alt">
            <div className="ccd-sl-sayfa" role="navigation" aria-label="Sayfalama">
              <button type="button" aria-label="Önceki sayfa" disabled={gecerliSayfa <= 1} onClick={() => degis({ p: gecerliSayfa - 1 > 1 ? String(gecerliSayfa - 1) : null }, true)}><Ikon ad="sol" boy={16} /></button>
              <span>{(gecerliSayfa - 1) * SAYFA + 1}–{Math.min(gecerliSayfa * SAYFA, suzulmus.length)} / {suzulmus.length}</span>
              <button type="button" aria-label="Sonraki sayfa" disabled={gecerliSayfa >= sayfaAdet} onClick={() => degis({ p: String(gecerliSayfa + 1) }, true)}><Ikon ad="sag" boy={16} /></button>
            </div>
            <span className="ccd-sl-alt-not">
              {faturasizVar ? 'Resmî + faturasız iki hane.' : 'Resmî hane.'} Satıra tıklayınca ürün kartı açılır · ↑↓ satır · Enter aç · boşluk hızlı bakış
            </span>
          </div>
        )}
      </div>

      {katmanlar}
    </div>
  );
}
